import { addDays, weekStart } from '@painpal/core';
import { ChevronLeft, ChevronRight, Undo2 } from 'lucide-react';
import { useRef, useState, type ReactNode, type RefObject } from 'react';
import type { SportFilter } from '../../api/keys';
import { usePrefetchWeek, useWeek } from '../../api/queries';
import { ErrorPanel, LoadingBlock } from '../../components/StateViews';
import { ToggleTabs } from '../../components/ToggleTabs';
import { useIsPhone } from '../../ui/useMediaQuery';
import { useSwipe } from '../../ui/useSwipe';
import { SportKey, WeekDayStrip, WeekFormLine, WeekTotals } from './WeekDayStrip';
import { trackPx, WEEK_METRICS, weekRangeLabel, weekStatus, type WeekMetric } from './weekSnapshotModel';
import styles from './weekSnapshot.module.css';

interface WeekNav {
  start: string; current: boolean; canPrev: boolean; canNext: boolean; prev: () => void; next: () => void; toCurrent: () => void;
  headingRef: RefObject<HTMLHeadingElement | null>;
}

// aria-disabled rather than disabled, so a keyboard user's focus stays on the arrow when it reaches either end.
function Arrow({ direction, nav }: { direction: 'prev' | 'next'; nav: WeekNav }) {
  const prev = direction === 'prev';
  const Icon = prev ? ChevronLeft : ChevronRight;
  const can = prev ? nav.canPrev : nav.canNext;
  const move = prev ? nav.prev : nav.next;
  return (
    <button
      type="button" className={styles.arrow} aria-label={prev ? 'Previous week' : 'Next week'}
      aria-disabled={!can} onClick={() => { if (can) move(); }}
    >
      <span className={styles.arrowBox}><Icon size={14} aria-hidden="true" /></span>
    </button>
  );
}

interface HeaderProps { nav: WeekNav; metric: WeekMetric; onMetric: (metric: WeekMetric) => void; totals: ReactNode }

function MetricToggle({ metric, onMetric }: Pick<HeaderProps, 'metric' | 'onMetric'>) {
  return <ToggleTabs label="Metric" options={WEEK_METRICS} value={metric} onChange={onMetric} className={styles.toggle} />;
}

function DesktopHeader({ nav, metric, onMetric, totals }: HeaderProps) {
  return (
    <div className={styles.header}>
      <div className={styles.nav}>
        <Arrow direction="prev" nav={nav} />
        <h2 className={styles.title} ref={nav.headingRef} tabIndex={-1}>{weekRangeLabel(nav.start)}</h2>
        <Arrow direction="next" nav={nav} />
        {nav.current
          ? <span className={styles.eyebrow}>THIS WEEK</span>
          : <button type="button" className={styles.thisWeek} onClick={nav.toCurrent}>THIS WEEK</button>}
      </div>
      <div className={styles.right}>
        {totals}
        <MetricToggle metric={metric} onMetric={onMetric} />
      </div>
    </div>
  );
}

function PhoneHeader({ nav, metric, onMetric, totals }: HeaderProps) {
  return (
    <>
      <div className={styles.header}>
        <Arrow direction="prev" nav={nav} />
        <div className={styles.middle}>
          <div className={styles.titleRow}>
            <h2 className={styles.title} ref={nav.headingRef} tabIndex={-1}>{weekRangeLabel(nav.start)}</h2>
            {nav.current
              ? <span className={styles.eyebrow}>THIS WEEK</span>
              : <button type="button" className={styles.back} aria-label="Back to this week" onClick={nav.toCurrent}><Undo2 size={12} aria-hidden="true" /></button>}
          </div>
          {totals}
        </div>
        <Arrow direction="next" nav={nav} />
      </div>
      <div className={styles.toggleRow}><MetricToggle metric={metric} onMetric={onMetric} /></div>
    </>
  );
}

export function WeekSnapshotCard({ today, poll, sport = 'all' }: { today: string; poll: () => number | false; sport?: SportFilter }) {
  const isPhone = useIsPhone();
  const thisWeek = weekStart(today);
  const [start, setStart] = useState(thisWeek);
  const [metric, setMetric] = useState<WeekMetric>('load');
  const [selected, setSelected] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const week = useWeek(start, sport, poll);
  const historyStart = week.data?.historyStart ?? null;
  const go = (next: string): void => { setStart(next); setSelected(null); };
  const nav: WeekNav = {
    start, current: start === thisWeek, canPrev: historyStart !== null && start > weekStart(historyStart), canNext: start < thisWeek,
    prev: () => go(addDays(start, -7)), next: () => go(addDays(start, 7)), headingRef,
    // The THIS WEEK button unmounts on the way, so focus moves to the heading instead of dropping to the page.
    toCurrent: () => { headingRef.current?.focus(); go(thisWeek); },
  };
  usePrefetchWeek(nav.canPrev ? addDays(start, -7) : null, sport);
  const swipe = useSwipe((direction) => {
    if (direction === 'prev' && nav.canPrev) nav.prev();
    if (direction === 'next' && nav.canNext) nav.next();
  });
  // A failed background refetch keeps the week it had; placeholder data is still the previous week, so it says so.
  const { data } = week;
  const dataCurrent = data?.start === thisWeek;
  const totals = data && <WeekTotals days={data.days} metric={metric} current={dataCurrent} selected={selected} sport={sport} compact={isPhone} />;
  const Header = isPhone ? PhoneHeader : DesktopHeader;
  return (
    <section className={styles.card} aria-label="Weekly snapshot" aria-busy={week.isPlaceholderData}>
      <Header nav={nav} metric={metric} onMetric={setMetric} totals={totals} />
      <p className="sr-only" role="status">{data && weekStatus(data.start, data.days, metric, dataCurrent, selected, sport)}</p>
      {week.isError && data === undefined && <ErrorPanel error={week.error} onRetry={() => void week.refetch()} />}
      {week.isPending && <LoadingBlock label="LOADING THE WEEK" height={trackPx(isPhone) + 40} />}
      {data && (
        <>
          <WeekDayStrip
            days={data.days} metric={metric} sport={sport} today={today} selected={selected} isPhone={isPhone} placeholder={week.isPlaceholderData}
            onSelect={(date) => setSelected((s) => (s === date ? null : date))} swipe={isPhone ? swipe : undefined}
          />
          <WeekFormLine end={data.end} previous={data.previousEnd} current={dataCurrent} isPhone={isPhone} filtered={sport !== 'all'} />
          {sport === 'all' && <SportKey days={data.days} />}
        </>
      )}
    </section>
  );
}
